import { AuthLayoutContainer } from 'components/Layouts/AuthLayout';
import { Typography, Button, IconGraphy, colors, Table } from '@leapeasy/ui-kit';
import { Box } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { setInviteData } from 'store/actions/authActions';
import { USER_ROLE } from 'data/constants/common_constants';
import { GlobalUXState } from 'components/GlobalUXState';
import { openLostInviteActionLink } from 'utils/lostInviteLinks';
import {
  getLostInviteStatusMessage,
  isPendingLostInvite,
  LOST_INVITE_STATUS,
} from 'utils/lostInviteStatus';
import { useNavigate } from 'react-router-dom';

const PROPERTY_COLUMNS = [
  {
    name: 'Property Name',
    key: 'name',
    options: {
      flex: '1 1 140px',
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value || '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'Property Type',
    key: 'building_type',
    options: {
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value || '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'Email',
    key: 'email_address',
    options: {
      flex: '1 1 160px',
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value || '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'Unit Count',
    key: 'total_of_units',
    options: {
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value ?? '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'Billing State',
    key: 'billingState',
    options: {
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value || '-'}
        </Typography>
      ),
    },
  },
  {
    name: 'Billing City',
    key: 'billingCity',
    options: {
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ color: colors.neutral[900] }}>
          {value || '-'}
        </Typography>
      ),
    },
  },
];

export const LostInviteResults = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const inviteDataState = useSelector((state) => state.getIn(['auth', 'inviteData']));
  const inviteData = inviteDataState ? inviteDataState.toJS() : null;

  const handleSearchAgain = () => {
    dispatch(setInviteData(null));
  };

  if (!inviteData) {
    return null;
  }

  const terminalStatus =
    inviteData.status === LOST_INVITE_STATUS.INVALID ||
    inviteData.status === LOST_INVITE_STATUS.DECLINED ||
    inviteData.status === LOST_INVITE_STATUS.ACCEPTED ||
    inviteData.status === LOST_INVITE_STATUS.EXPIRED ||
    inviteData.status === LOST_INVITE_STATUS.SENT
      ? inviteData.status
      : null;

  if (terminalStatus) {
    return (
      <AuthLayoutContainer showGrayLogo>
        <Box display="flex" flexDirection="column" gap="24px">
          <Button
            variant="tertiary"
            style={{ width: 'fit-content', paddingLeft: '12px', paddingRight: '12px' }}
            onClick={handleSearchAgain}
          >
            <IconGraphy icon="Arrow.ArrowBack" width={11} height={11} />
            Search again
          </Button>
          <Typography variant="h2" style={{ fontWeight: 500, color: colors.purple[900] }}>
            Find your invitation
          </Typography>
          <Typography variant="body1" style={{ color: colors.purple[900] }}>
            {getLostInviteStatusMessage(terminalStatus)}
          </Typography>
          {terminalStatus === LOST_INVITE_STATUS.ACCEPTED && (
            <Button variant="primary" size="medium" onClick={() => navigate('/login')}>
              Sign in
            </Button>
          )}
          {terminalStatus !== LOST_INVITE_STATUS.ACCEPTED && (
            <Button variant="tertiary" size="medium" onClick={handleSearchAgain}>
              Search again
            </Button>
          )}
        </Box>
      </AuthLayoutContainer>
    );
  }

  if (!isPendingLostInvite(inviteData)) {
    return null;
  }

  const propertyRows = inviteData.property_data || [];
  const tableData = propertyRows.map((row) => [
    row.name || '-',
    row.building_type || '-',
    row.email_address || '-',
    row.total_of_units ?? '-',
    row.billingState || '-',
    row.billingCity || '-',
  ]);

  const requestorLabel = inviteData.requestor_email
    ? `${inviteData.requestor_email}${
        inviteData.requestor_role
          ? ` (${USER_ROLE[inviteData.requestor_role] || inviteData.requestor_role})`
          : ''
      }`
    : '';

  return (
    <AuthLayoutContainer showGrayLogo>
      <Box display="flex" flexDirection="column" gap="24px">
        <Button
          variant="tertiary"
          style={{ width: 'fit-content', paddingLeft: '12px', paddingRight: '12px' }}
          onClick={handleSearchAgain}
        >
          <IconGraphy icon="Arrow.ArrowBack" width={11} height={11} />
          Search again
        </Button>

        <Typography variant="h2" style={{ fontWeight: 500, color: colors.purple[900] }}>
          Find your invitation
        </Typography>

        <Typography variant="body1" style={{ color: colors.neutral[900], fontWeight: 400 }}>
          {requestorLabel} has invited you with the following properties.
        </Typography>

        {propertyRows.length > 0 && (
          <TableWrap>
            <Table
              showEmptyState
              emptyContent={
                <GlobalUXState
                  variant="noData"
                  compact
                  description="No properties were attached to this invitation."
                />
              }
              columns={PROPERTY_COLUMNS}
              data={tableData}
              style={{ width: '100%' }}
            />
          </TableWrap>
        )}

        {inviteData.accept_link && inviteData.decline_link && (
          <ActionsRow>
            <Button
              variant="tertiary"
              size="medium"
              onClick={() => openLostInviteActionLink(inviteData.decline_link)}
            >
              Decline
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={() => openLostInviteActionLink(inviteData.accept_link)}
            >
              Accept
            </Button>
          </ActionsRow>
        )}
      </Box>
    </AuthLayoutContainer>
  );
};

const TableWrap = styled(Box)`
  border: 1px solid #eaeaea;
  border-radius: 12px;
  overflow: hidden;
  max-height: 320px;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;

  button {
    min-width: 120px;
    width: auto;
  }
`;
